import {
    ContractWarning,
    CustomerEventWarning,
    DayOffWarning,
    DocumentRetrievalWarning,
    LeadWarning,
    POCServiceWarning,
    ServiceMissingUUIDWarning,
    ServiceWaitingConfirmWarning,
    SubcontractDocumentRetrievalWarning,
    TicketWarning,
    WarningItem,
} from '@/types/warnings'
import { generatePathDatacentral, paths } from '@/utils/path'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { buildAlertMetadata } from './utils'
import { Text, Badge } from '@vnetwork/datacentral-ui/primitives'

interface AlertDetailProps {
    alert: WarningItem
}

const AlertDetail: React.FC<AlertDetailProps> = ({ alert }) => {
    const { t } = useTranslation()

    const getNavigationPath = (alert: WarningItem): string => {
        switch (alert.type) {
            case 'contract_expired':
            case 'contract_expiring': {
                const contractData = alert.data as ContractWarning
                return generatePathDatacentral(paths.contractDetail, {
                    id: contractData?.contract_id,
                })
            }
            case 'dayoff_pending': {
                const dayOffData = alert.data as DayOffWarning
                return `/dayoffs/${dayOffData?.day_off_id}`
            }
            case 'document_retrieval_upcoming':
            case 'document_retrieval_overdue': {
                const documentRetrievalData =
                    alert.data as DocumentRetrievalWarning
                return generatePathDatacentral(paths.contractDetail, {
                    id: documentRetrievalData?.contract_id,
                })
            }
            case 'subcontract_document_retrieval_overdue':
            case 'subcontract_document_retrieval_upcoming': {
                const subcontractDocumentRetrievalData =
                    alert.data as SubcontractDocumentRetrievalWarning
                return generatePathDatacentral(paths.contractDetail, {
                    id: subcontractDocumentRetrievalData?.contract_id,
                })
            }
            case 'poc_service_overdue':
            case 'poc_service_upcoming': {
                const pocServiceData = alert.data as POCServiceWarning
                return (
                    paths.services +
                    `?tab=service-poc&filters=${btoa(
                        JSON.stringify({
                            product_group_name: [
                                pocServiceData?.product_group_name,
                            ],
                            service_status: [pocServiceData?.service_status],
                        })
                    )}`
                )
            }
            case 'service_waiting_confirm': {
                const serviceWaitingConfirmData =
                    alert.data as ServiceWaitingConfirmWarning
                return (
                    paths.services +
                    `?tab=waiting-confirm&filters=${btoa(
                        JSON.stringify({
                            product_group_name: [
                                serviceWaitingConfirmData?.product_group_name,
                            ],
                        })
                    )}`
                )
            }
            case 'service_missing_uuid': {
                const serviceMissingUUIDData =
                    alert.data as ServiceMissingUUIDWarning
                return generatePathDatacentral(paths.serviceDetail, {
                    id: serviceMissingUUIDData?.service_id,
                })
            }
            case 'lead_unassigned': {
                // Mở thẳng chi tiết lead (modal overlay trên list) qua query
                // param contact_source_id — giống hành vi click 1 dòng trong
                // danh sách và giống mục lead_stale.
                const leadData = alert.data as LeadWarning
                return `${paths.customerMkt}?contact_source_id=${leadData?.contact_source_id}`
            }
            case 'lead_stale': {
                // Dùng query param contact_source_id để mở modal chi tiết (overlay
                // trên list) — đồng nhất với lead_unassigned; tránh route trang
                // riêng /contacts/{id} có giao diện khác.
                const leadData = alert.data as LeadWarning
                return `${paths.customerMkt}?contact_source_id=${leadData?.contact_source_id}`
            }
            case 'ticket_escalated': {
                const ticketData = alert.data as TicketWarning
                return generatePathDatacentral(paths.ticketDetail, {
                    id: ticketData?.ticket_id,
                })
            }
            case 'customer_event_upcoming': {
                const eventData = alert.data as CustomerEventWarning
                const month = eventData.event_month
                const safeMonth =
                    Number.isInteger(month) && month >= 1 && month <= 12
                        ? month
                        : new Date().getMonth() + 1
                return `${paths.accountCustomerEvents}?month=${safeMonth}`
            }
            default:
                return '#'
        }
    }

    // Build alert metadata
    const alertMetadata = buildAlertMetadata(alert, t)

    const severityTone =
        alert.severity === 'high'
            ? 'danger'
            : alert.severity === 'medium'
              ? 'warning'
              : 'info'

    return (
        <Link
            to={getNavigationPath(alert)}
            className="block space-y-1 border-t border-border-subtle py-3 ps-10 pe-4 transition-colors hover:bg-accent"
        >
            <Text
                as="div"
                size="sm"
                weight="medium"
                tone="primary"
                className="line-clamp-2"
            >
                {alertMetadata.label}
            </Text>
            {alertMetadata.subtitle && (
                <Text
                    as="div"
                    size="xs"
                    weight="medium"
                    className="line-clamp-2"
                >
                    {alertMetadata.subtitle}
                </Text>
            )}
            <div className="flex items-center justify-between gap-3 pt-0.5">
                <Text as="div" size="xs" tone="muted" truncate>
                    {alertMetadata.description}
                </Text>
                <Badge size="sm" tone={severityTone} className="shrink-0">
                    {t(`alerts.severity.${alert.severity}`)}
                </Badge>
            </div>
        </Link>
    )
}

export default AlertDetail
